/**
 * use-books — books / categories / tags / chapters API hooks（ADM-BOK-S001~S014）
 */

import type {
  BookAppStatusResponse,
  BookListQuery,
  BookOperationLogItem,
  BookOperationLogQuery,
  CategoryResponse,
  ChapterResponse,
  CoverUploadResponse,
  CreateCategoryRequest,
  CreateChapterWithContentRequest,
  CreateNovelLanguageRequest,
  CreateNovelRequest,
  CreateTagRequest,
  GetChapterContentResponse,
  NovelGroupDetail,
  NovelGroupSummary,
  NovelLanguageSummary,
  NovelResponse,
  NovelStatsResponse,
  NovelVisibility,
  PatchChapterMetadataRequest,
  PublishChapterResponse,
  PublishQueueResponse,
  PublishScheduleResponse,
  RebaseChapterDraftRequest,
  RepublishChapterRequest,
  SetAppOverrideRequest,
  SetGroupDefaultVisibilityRequest,
  SetNovelVisibilityRequest,
  SetPublishScheduleRequest,
  SystemEventLogItem,
  TagResponse,
  TranslateNovelFieldsRequest,
  TranslateNovelFieldsResponse,
  UpdateCategoryRequest,
  UpdateNovelGroupRequest,
  UpdateNovelRequest,
  UpdateTagRequest,
} from '@novel-admin/shared';
import { type OffsetPagedResult, apiRequest, apiRequestOffsetPaged } from '~/api/client';
import { downloadAuthedCsv } from '~/api/download-authed-csv';
import { uploadImageFile } from '~/api/upload-image';

/** 下载端点基址（裸 CSV 流，手写 fetch 拼绝对 URL；apiRequest 的 envelope 解析不适用）。 */
const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

/**
 * App 级覆盖行（GET /books/:id/overrides 返回）。
 *
 * server `listAppOverrides` 直接 `db.select()` 返回 novel_app_overrides 行 → drizzle 属性名为 camelCase
 * （`appId` / `visibility` / ...，非 snake_case 包装）。shared 未为该读出口定型 DTO（route 声明 `unknown[]`，
 * 见 docs/plans §五），故在 web 侧按实际返回形状声明本类型，只消费 override 写路径必需的字段（appId / visibility）。
 * visibility 闭集为四档全集（dd#941 起层③ 允许 PUBLIC —— 三态下 override 禁 LISTED 的约束已解除）。
 */
export interface AppOverrideRow {
  appId: string;
  visibility: NovelVisibility;
}

export const categoriesApi = {
  list: () => apiRequest<CategoryResponse[]>({ method: 'GET', path: '/api/v1/categories' }),
  create: (body: CreateCategoryRequest) =>
    apiRequest<CategoryResponse>({ method: 'POST', path: '/api/v1/categories', body }),
  update: (id: string, body: UpdateCategoryRequest) =>
    apiRequest<CategoryResponse>({ method: 'PATCH', path: `/api/v1/categories/${id}`, body }),
  // 删除分类（物理删；被书引用则后端拒删 RESOURCE_CONFLICT）
  remove: (id: string) =>
    apiRequest<{ ok: true }>({ method: 'DELETE', path: `/api/v1/categories/${id}` }),
};

export const tagsApi = {
  list: () => apiRequest<TagResponse[]>({ method: 'GET', path: '/api/v1/tags' }),
  create: (body: CreateTagRequest) =>
    apiRequest<TagResponse>({ method: 'POST', path: '/api/v1/tags', body }),
  update: (id: string, body: UpdateTagRequest) =>
    apiRequest<TagResponse>({ method: 'PATCH', path: `/api/v1/tags/${id}`, body }),
  // 删除标签（物理删；被书引用则后端拒删 RESOURCE_CONFLICT）
  remove: (id: string) =>
    apiRequest<{ ok: true }>({ method: 'DELETE', path: `/api/v1/tags/${id}` }),
};

export const booksApi = {
  // 书籍文案翻译（编辑页「从英文翻译」）：复用 DeepL 通道翻译 texts → 目标 locale（不写库，译文供填表）
  translate: (body: TranslateNovelFieldsRequest) =>
    apiRequest<TranslateNovelFieldsResponse>({
      method: 'POST',
      path: '/api/v1/books/translate',
      body,
    }),
  // 列表：按书组聚合（一本书一条）。GET /api/v1/books 是 offset 分页（okOffsetPaged，total/page/limit/hasMore
  // 在 envelope 顶层，默认 limit=200）。listOffsetPaged 解出 data + 真实 total，供调用方显示真实总数 / 总页数 + 跳任意页。
  // admin 后台 endpoint 免 C 端 cursor 契约（dev-docs 40-分页与排序 §1.1.1）。list 仅取首页数组供不关心分页的内部复用。
  // filters（ADM-BOK-S006）服务端下推：q/visibility 单值；source/category/tag/lang/serial_status 多值
  // → client 序列化为重复 query 参数（?source=ORIGINAL&source=LICENSED）。空数组 / undefined 由 client 跳过。
  listOffsetPaged: (
    page?: number,
    limit?: number,
    filters?: BookListQuery,
  ): Promise<OffsetPagedResult<NovelGroupSummary>> =>
    apiRequestOffsetPaged<NovelGroupSummary>({
      method: 'GET',
      path: '/api/v1/books',
      query: { ...(page ? { page } : {}), ...(limit ? { limit } : {}), ...(filters ?? {}) },
    }),
  list: () => apiRequest<NovelGroupSummary[]>({ method: 'GET', path: '/api/v1/books' }),
  // 书组详情：书组级资料 + 所有 per-locale 语言版本（A6 §15.2；`id` = novel_groups.id，dd#970）
  detail: (groupId: string) =>
    apiRequest<NovelGroupDetail>({ method: 'GET', path: `/api/v1/book-groups/${groupId}` }),
  // 单语言行原始字段（编辑表单回填；A6 §15.3，`id` = per-locale novels.id）。
  //
  // **路径用 `/books/:id/language` 而非 `/books/:id`**，理由是**语义分辨**不是滚动兼容：
  // 同一个 `/books/:id` 在旧 Worker 上返书组详情、在新 Worker 上返单行详情，两代 shape 不同而
  // 状态码都是 200，撞上就是静默解析错值。别名让本调用点的意图恒为「取单行」，与部署到哪一代无关
  // （新 Worker 两条指向同一 handler，见 routes/v1/books/index.ts 的兼容别名）。
  //
  // ⚠️ **别把这条别名理解成「Web-first 滚动窗口的兼容层」——那个窗口里没有可服务的旧 Worker**：
  // 本刀依赖 C 端 migration 0184，而 0184 一落地，旧 Worker 读的 `novels.original_title` /
  // `read_count` / `source` 等列已 DROP（42703）、`novel_group_codes` 已改名（42P01），书籍列表与
  // 详情一律 500。故「0184 应用 → 新 admin Worker 上线」之间书籍域必然不可用，与前端发哪一版无关；
  // 本仓 `deploy:prod` 的 web→server 顺序只是让前端也落在同一个窗口里，不制造额外故障面。
  // 缩短窗口的办法是这一版**手动倒序发**（先 server 后 web），不是在前端加旧 DTO 归一层。
  language: (id: string) =>
    apiRequest<NovelResponse>({ method: 'GET', path: `/api/v1/books/${id}/language` }),
  create: (body: CreateNovelRequest) =>
    apiRequest<NovelResponse>({ method: 'POST', path: '/api/v1/books', body }),
  update: (id: string, body: UpdateNovelRequest) =>
    apiRequest<NovelResponse>({ method: 'PATCH', path: `/api/v1/books/${id}`, body }),
  // 改书组级字段（A6 §16.4；cn_title / 原作信息 / source / 展示基数）—— dd#970 后这批字段
  // 不再经 PATCH /books/:id，那边提交它们一律 VALIDATION_FAILED
  updateGroup: (groupId: string, body: UpdateNovelGroupRequest) =>
    apiRequest<NovelGroupDetail>({
      method: 'PATCH',
      path: `/api/v1/book-groups/${groupId}`,
      body,
    }),
  // 复制书籍为草稿（复制 root 元数据，不复制章节）→ 新 HIDDEN 草稿书组
  duplicate: (id: string) =>
    apiRequest<NovelResponse>({ method: 'POST', path: `/api/v1/books/${id}/duplicate` }),
  // 新增语言版本（A6 §17）：在书组下新建一行 novels（`groupId` = novel_groups.id，dd#970）
  createLanguage: (groupId: string, body: CreateNovelLanguageRequest) =>
    apiRequest<NovelLanguageSummary>({
      method: 'POST',
      path: `/api/v1/book-groups/${groupId}/languages`,
      body,
    }),
  // 层② 单书覆盖写（A6 §12.2）。`visibility: null` 是有意义的入参 —— 清除本层覆盖、回落层①，
  // 与显式配 PUBLIC 不同，故请求体恒带该键、不做 undefined 省略。
  setVisibility: (id: string, body: SetNovelVisibilityRequest) =>
    apiRequest<NovelResponse>({ method: 'POST', path: `/api/v1/books/${id}/visibility`, body }),
  // 层① 书组默认层写（A6 §12.1，ADM-BOK-S021）：入口 id 是书组 id，生效范围全书组全语言版。
  setGroupDefaultVisibility: (groupId: string, body: SetGroupDefaultVisibilityRequest) =>
    apiRequest<NovelGroupDetail>({
      method: 'POST',
      path: `/api/v1/book-groups/${groupId}/group-visibility`,
      body,
    }),
  setOverride: (id: string, body: SetAppOverrideRequest) =>
    apiRequest<{ ok: true }>({ method: 'POST', path: `/api/v1/books/${id}/overrides`, body }),
  removeOverride: (id: string, appId: string) =>
    apiRequest<{ ok: true }>({
      method: 'DELETE',
      path: `/api/v1/books/${id}/overrides`,
      body: { app_id: appId },
    }),
  listOverrides: (id: string) =>
    apiRequest<AppOverrideRow[]>({ method: 'GET', path: `/api/v1/books/${id}/overrides` }),
  // ADM-BOK 上下架 tab 三层读模型（GET /api/v1/books/:id/app-status；只读派生，BookAppStatusResponse，A6 §10/§11）。
  // 顶层给层① group_default_visibility + 层② novel_visibility + 原因池 global_reason_i18n；
  // apps[] = 投放名单 ∪ orphan override App，每行给 effective_visibility 与它来自哪一层（source_layer）。
  appStatus: (id: string) =>
    apiRequest<BookAppStatusResponse>({ method: 'GET', path: `/api/v1/books/${id}/app-status` }),
  // 书组运营统计（A6 §19；`groupId` = novel_groups.id，dd#970）
  stats: (groupId: string) =>
    apiRequest<NovelStatsResponse>({ method: 'GET', path: `/api/v1/book-groups/${groupId}/stats` }),
  // ADM-BOK-S015 运营操作 / 系统事件日志（GET /api/v1/books/:id/logs；offset 分页）。
  // type=operation → admin_audit_logs；type=system → novel_system_events（SystemEventLogItem）。
  listLogs: <T extends BookOperationLogItem | SystemEventLogItem = BookOperationLogItem>(
    id: string,
    query: BookOperationLogQuery,
    page?: number,
    limit?: number,
  ): Promise<OffsetPagedResult<T>> =>
    apiRequestOffsetPaged<T>({
      method: 'GET',
      path: `/api/v1/books/${id}/logs`,
      query: {
        ...(query.type ? { type: query.type } : {}),
        ...(query.from ? { from: query.from } : {}),
        ...(query.to ? { to: query.to } : {}),
        ...(page ? { page } : {}),
        ...(limit ? { limit } : {}),
      },
    }),
  // 导出书籍列表 CSV（与列表同筛选；裸 CSV 流 → 认证下载）。filters 透传 q/visibility/source/category/tag/lang/serial_status。
  exportListCsv: (filters: BookListQuery = {}) => {
    const qs = new URLSearchParams();
    if (filters.q) qs.set('q', filters.q);
    if (filters.visibility) qs.set('visibility', filters.visibility);
    if (filters.is_recommended !== undefined)
      qs.set('is_recommended', String(filters.is_recommended));
    for (const s of filters.source ?? []) qs.append('source', s);
    for (const c of filters.category ?? []) qs.append('category', c);
    for (const t of filters.tag ?? []) qs.append('tag', t);
    for (const l of filters.lang ?? []) qs.append('lang', l);
    for (const ss of filters.serial_status ?? []) qs.append('serial_status', ss);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return downloadAuthedCsv(`${API_BASE || ''}/api/v1/books/export${suffix}`, 'books.csv');
  },
  // ADM-BOK-S015 导出当前筛选的操作日志为 CSV（裸流 → 走认证下载；X-Export-Truncated 透出超限提示）。
  downloadLogsCsv: (id: string, query: BookOperationLogQuery, fallbackName: string) => {
    const qs = new URLSearchParams();
    if (query.type) qs.set('type', query.type);
    if (query.from) qs.set('from', query.from);
    if (query.to) qs.set('to', query.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return downloadAuthedCsv(
      `${API_BASE || ''}/api/v1/books/${id}/logs/export${suffix}`,
      fallbackName,
    );
  },
  // 封面上传（ADM-BOK-S003）：multipart → 返相对 ref（随后随保存写 novels.cover_url）。走共享 uploadImageFile
  // （apiRequest 只发 JSON、破坏 multipart boundary，故单独的 FormData fetch + Bearer + 401 清 session）。
  uploadCover: (file: File): Promise<CoverUploadResponse> =>
    uploadImageFile<CoverUploadResponse>('/api/v1/books/cover-upload', file),
};

export const chaptersApi = {
  // per-locale 模型下章节挂在某一 novel_id 下（locale 由 novel_id 隐含，不再传 locale）
  list: (novelId: string) =>
    apiRequest<ChapterResponse[]>({
      method: 'GET',
      path: `/api/v1/books/${novelId}/chapters`,
    }),
  // 新建章节（运营手动撰写）— 代理创建：发元数据 + 正文纯文本，server 生成 id/算 sha256/word_count + PUT R2。
  // 唯一创建入口（plain POST /:id/chapters 接受 client content_r2_key 的旁路已移除，见 server 路由注释）。
  createWithContent: (novelId: string, body: CreateChapterWithContentRequest) =>
    apiRequest<ChapterResponse>({
      method: 'POST',
      path: `/api/v1/books/${novelId}/chapters/with-content`,
      body,
    }),
  // 章节元数据编辑（仅 title/cost/is_free）；已发布章保存为待重发布草稿，不覆盖线上快照。
  update: (chapterId: string, body: PatchChapterMetadataRequest) =>
    apiRequest<ChapterResponse>({
      method: 'PATCH',
      path: `/api/v1/books/chapters/${chapterId}`,
      body,
    }),
  publish: (chapterId: string) =>
    apiRequest<PublishChapterResponse>({
      method: 'POST',
      path: `/api/v1/books/chapters/${chapterId}/publish`,
    }),
  republish: (chapterId: string, body: RepublishChapterRequest) =>
    apiRequest<ChapterResponse>({
      method: 'POST',
      path: `/api/v1/books/chapters/${chapterId}/republish`,
      body,
    }),
  rebaseDraft: (chapterId: string, body: RebaseChapterDraftRequest) =>
    apiRequest<ChapterResponse>({
      method: 'POST',
      path: `/api/v1/books/chapters/${chapterId}/rebase-draft`,
      body,
    }),
  // 章节正文读（编辑器回填）：GET /books/chapters/:id/content → { content, content_version, content_sha256 }
  getContent: (chapterId: string) =>
    apiRequest<GetChapterContentResponse>({
      method: 'GET',
      path: `/api/v1/books/chapters/${chapterId}/content`,
    }),
  // 章节正文写（运营编辑）：未发布章直接更新；已发布章以线上 snapshot + draft 双 CAS 保存草稿。
  setContent: (
    chapterId: string,
    content: string,
    expectedContentVersion: number,
    expectedBaseSnapshotVersion?: number,
  ) =>
    apiRequest<ChapterResponse>({
      method: 'PUT',
      path: `/api/v1/books/chapters/${chapterId}/content`,
      body: {
        content,
        expected_content_version: expectedContentVersion,
        ...(expectedBaseSnapshotVersion === undefined
          ? {}
          : { expected_base_snapshot_version: expectedBaseSnapshotVersion }),
      },
    }),
  getPublishSchedule: (novelId: string) =>
    apiRequest<PublishScheduleResponse>({
      method: 'GET',
      path: `/api/v1/books/${novelId}/publish-schedule`,
    }),
  setPublishSchedule: (novelId: string, body: SetPublishScheduleRequest) =>
    apiRequest<PublishScheduleResponse>({
      method: 'PUT',
      path: `/api/v1/books/${novelId}/publish-schedule`,
      body,
    }),
  getPublishQueue: (novelId: string) =>
    apiRequest<PublishQueueResponse | null>({
      method: 'GET',
      path: `/api/v1/books/${novelId}/publish-queue`,
    }),
  cancelPublishQueue: (novelId: string) =>
    apiRequest<{ ok: true }>({
      method: 'DELETE',
      path: `/api/v1/books/${novelId}/publish-queue`,
    }),
};
